import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pagination } from "@pd/pd-ui-kit";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import Wrapper from "~/shared/ui/Wrapper";
import { useGetIvideonDealerDocs } from "~/entity/Ivideon";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { useGetProfileQuery } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2 } from "~/shared/ui/TableV2";

import ModalsComponent from "./components/ModalsComponent";
import TitleNavigateGroup from "./components/TitleNavigateGroup";
import FilterRegisterOfDocuments from "./FilterRegisterOfDocuments";
import { formatForCodeList, fromServer } from "./helper";
import { getRegisterOfDocumentsColumns } from "./model/tableAdapter";
import { DEFAULT_ACT_STATUSES, TypeOpenModal } from "./options";

import s from "./RegisterOfDocuments.less";

export interface CodeListIProps {
  id: number;
  licenseCode: string;
  tariffName: string;
  dateAct: string;
}

export default function RegisterOfDocuments() {
  const { t } = useTranslation();
  const { count, page, openModal, actId, dealerActId } = useURLParamsGetter<{
    count: string;
    page: string;
    openModal: string;
    actId: string;
    dealerActId: string;
  }>();
  const setURLParams = useURLParamsSetter();
  const { hasPermission } = usePermissions();
  const deacceptGroups = hasPermission("ivideonGroups.deacceptGroups");
  const createDealerLicenses = hasPermission(
    "ivideonGroups.createDealerLicenses",
  );
  const getLicenses = hasPermission("ivideon.getLicenses");
  const getLicensesReport = hasPermission("ivideon.getLicensesReport");

  const [codeList, setCodeList] = useState<CodeListIProps[] | []>([]);
  const {
    data,
    isLoading,
    getRequestParams,
    refetch: dataRefetch,
  } = useGetIvideonDealerDocs();
  const history = useHistory();
  const getProfile = useGetProfileQuery({});
  const ivideonSuccess = get(
    getProfile,
    "data.UserGetter.getProfile.ivideon_access",
  );
  useEffect(() => {
    if (!ivideonSuccess) {
      history.push("/");
    }
  }, [ivideonSuccess]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.append("count", count ?? "25");
    params.append("page", page ?? "0");
    DEFAULT_ACT_STATUSES.forEach((status) =>
      params.append("actStatus[]", status),
    );
    getRequestParams(params.toString());
  }, [count, page, getRequestParams]);

  const openModalState = useCallback(
    (prop: TypeOpenModal, actId: number, dealerActId: number) => {
      setURLParams({ openModal: prop, actId, dealerActId });
    },
    [setURLParams],
  );

  const onHistoryNavigate = useCallback(
    (dealerId: number, dealerName: string) => {
      history.push(
        `/ivideon/register-of-documents/history?dealerId=${dealerId}&dealerName=${dealerName}`,
      );
    },
    [history],
  );

  const columns = useMemo(
    () =>
      getRegisterOfDocumentsColumns(
        t,
        openModalState,
        onHistoryNavigate,
        deacceptGroups,
        createDealerLicenses,
        getLicenses,
      ),
    [
      t,
      openModalState,
      onHistoryNavigate,
      deacceptGroups,
      createDealerLicenses,
      getLicenses,
    ],
  );

  useEffect(() => {
    if (openModal === "code" && data?.dealers.length) {
      setCodeList(formatForCodeList({ data: data, actId, dealerActId }));
    }
  }, [openModal, actId, dealerActId, data]);

  const { handleCount, handlePage } = usePaginationParams("urlParams");

  return (
    <>
      <Wrapper.Content
        title={"Реестр документов"}
        controlComponent={
          <TitleNavigateGroup getLicensesReport={getLicensesReport} />
        }
        additionalClassName={s.fullWidth}
        filterComponent={
          <FilterRegisterOfDocuments
            getRequestParams={getRequestParams}
            page={page ? page.toString() : "0"}
            count={count ? count.toString() : "25"}
          />
        }
      >
        <TableV2
          wrapperClassName={s.tableWrapper}
          columns={columns}
          dataSource={data?.dealers?.length ? fromServer(data.dealers) : []}
          loading={isLoading}
          rowKey="id"
          scroll={{ x: "max-content" }}
        />
        <Pagination
          count={Number(count) || 25}
          page={(Number(page) || 0) + 1}
          totalCount={data?.totalCount ?? 1}
          onChangeCount={handleCount}
          onChangePage={handlePage}
        />
      </Wrapper.Content>
      <ModalsComponent
        openModal={openModal}
        isLoadingLicenses={isLoading}
        codeList={codeList}
        refetch={dataRefetch}
      />
    </>
  );
}
